import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Typography, Form, Input, Button, Space, Row, Col, Modal, message, InputRef } from 'antd';
import { BarChartOutlined } from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

/**
 * Valida se uma string é um UUID válido.
 */
const isValidUUID = (str: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str.trim());
};

/**
 * Página de Análise de ID para Push.
 * 
 * Permite que o usuário informe o ID de um push e navegue para a tela de detalhes.
 */
const ReportsContent: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const pushIdRef = useRef<InputRef>(null);
  const [showExamplesModal, setShowExamplesModal] = useState(false);
  const [validationError, setValidationError] = useState<string | undefined>(undefined);

  /**
   * Exemplos de IDs de push para referência.
   */
  const exampleIds = [
    "203f8cfb-2dcc-4231-8ec7-b5c99d13ea8c",
    "fb0588a2-f808-44de-a2e6-e64cfcb31b8d",
  ];

  /**
   * Valida o ID do push.
   */
  const validatePushId = (value: string): { valid: boolean; error?: string } => {
    if (!value || value.trim().length === 0) {
      return { valid: false, error: "Por favor, digite o ID do push" };
    }

    const trimmedId = value.trim();

    if (!isValidUUID(trimmedId)) {
      return { valid: false, error: `ID inválido: ${trimmedId}. Formato esperado: UUID` };
    }

    return { valid: true };
  };

  /**
   * Handler para submeter o formulário.
   */
  const handleSubmit = async (values: { pushId: string }) => {
    const pushId = values.pushId?.trim();

    if (!pushId) {
      message.error('Por favor, digite o ID do push');
      return;
    }

    const validation = validatePushId(pushId);
    if (!validation.valid) {
      setValidationError(validation.error);
      message.error(validation.error);
      return;
    }

    setValidationError(undefined);
    // Navega para a tela de detalhes do push
    navigate(`/push-details/${pushId}`);
  };

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <Title level={2} style={{ marginBottom: 0 }}>
              <BarChartOutlined /> Análise de ID
            </Title>
            <Button type="primary" onClick={() => setShowExamplesModal(true)}>
              Ver exemplos
            </Button>
          </div>
          <Paragraph style={{ marginBottom: 8, fontSize: '13px' }}>
            Digite o ID do push para visualizar a análise detalhada com requests, callbacks e monitoramentos.
          </Paragraph>
        </div>

        <Modal
          title="Exemplos de IDs de Push"
          open={showExamplesModal}
          onCancel={() => setShowExamplesModal(false)}
          footer={[
            <Button key="close" onClick={() => setShowExamplesModal(false)}>
              Fechar
            </Button>,
          ]}
          width={800}
        >
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Text strong>Formato esperado (UUID):</Text>
            <TextArea
              value={exampleIds.join('\n')}
              readOnly
              rows={exampleIds.length}
              style={{ fontFamily: 'monospace', resize: 'none', overflow: 'hidden' }}
            />
          </Space>
        </Modal>

        <Form form={form} onFinish={handleSubmit} layout="vertical" style={{ marginTop: 8 }}>
          <Form.Item
            name="pushId"
            label="ID do Push"
            validateStatus={validationError ? 'error' : undefined}
            help={validationError}
            rules={[
              { required: true, message: 'Por favor, digite o ID do push!' },
              {
                validator: (_, value) => {
                  const validation = validatePushId(value);
                  if (!validation.valid) {
                    return Promise.reject(new Error(validation.error));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              ref={pushIdRef}
              placeholder="Digite o ID do push (UUID)"
              style={{ fontFamily: 'monospace' }}
              onChange={(e) => {
                const value = e.target.value;

                if (value.trim().length === 0) {
                  setValidationError(undefined);
                  return;
                }

                const validation = validatePushId(value);
                if (!validation.valid) {
                  setValidationError(validation.error);
                } else {
                  setValidationError(undefined);
                }
              }}
            />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" size="large" block icon={<BarChartOutlined />}>
              Ver Análise
            </Button>
          </Form.Item>
        </Form>
      </Space>
    </Card>
  );
};

/**
 * Componente principal da página Reports.
 */
const Reports: React.FC = () => {
  return (
    <ReportsContent />
  );
};

export default Reports;

